// IssuesCard v0.4.3 — collapsible, persisted panel state per visitor.
// Mirrors LongTermIssuesCard with compact rows, one clear severity pill, and
// high-contrast provider/status/action affordances.
import { useId } from 'react';
import type { ProviderItem, StateResponse } from '../lib/api.js';
import { isLongTerm, timeAgo } from '../lib/formatTime.js';
import { providerDisplayName } from '../lib/providerDisplayName.js';
import { getProviderSeverityTone, resolveProviderState } from '../lib/providerState.js';
import { statusPageUrl } from '../lib/statusPageUrl.js';
import ExternalIndicator from './ExternalIndicator.js';
import ExternalStatusIcon from './ExternalStatusIcon.js';
import ProviderInfoTooltip from './ProviderInfoTooltip.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import CommandPanel from './ui/CommandPanel.js';
import SectionHeader from './ui/SectionHeader.js';
import StatusGlow from './ui/StatusGlow.js';

const SEVERITY_LABEL: Record<number, string> = {
  1: 'DEG',
  2: 'MAJ',
  3: 'DOWN',
};

interface Props {
  all: ProviderItem[];
  state: StateResponse['providers'];
}

export default function IssuesCard({ all, state }: Props) {
  const getState = (p: ProviderItem) => resolveProviderState(state, p);
  const [expanded, setExpanded] = usePersistedPanelState('panel:active-issues', true);
  const contentId = useId();
  const issues = all
    .map((p) => {
      const providerState = getState(p);
      return {
        p,
        severity: providerState?.severity ?? 0,
        msg: providerState?.message ?? '',
        since: providerState?.since,
        updatedAt: providerState?.updated_at,
        tone: getProviderSeverityTone(state, p),
      };
    })
    .filter((row) => row.severity > 0 && !isLongTerm(row.updatedAt ?? row.since))
    .sort(
      (a, b) => a.p.tier - b.p.tier || b.severity - a.severity || a.p.name.localeCompare(b.p.name),
    );

  if (issues.length === 0) {
    const anyRed = all.some((p) => getProviderSeverityTone(state, p) > 0);
    const headline = anyRed
      ? 'NO NEW ISSUES (LONG-TERM ITEMS BELOW)'
      : 'ALL TRACKED PROVIDERS REPORTING NORMAL';

    return (
      <CommandPanel className="border border-ok/60 bg-ok/10">
        <SectionHeader
          title={
            <span className="flex items-center gap-2">
              <span aria-hidden="true" className="text-ok">
                ●
              </span>
              <span>{headline}</span>
            </span>
          }
          right={<span className="text-white font-normal">{all.length} monitored</span>}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          contentId={contentId}
        />
        {expanded && <div className="px-3 py-2 text-xs text-white">No active issues</div>}
      </CommandPanel>
    );
  }

  return (
    <CommandPanel className="border border-bad/70 bg-bad/10">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className="text-bad animate-pulse">
              ⚠
            </span>
            <span>ACTIVE ISSUES</span>
          </span>
        }
        right={<span className="text-white font-normal">{issues.length} of {all.length}</span>}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <div className="divide-y divide-border/60 text-xs font-mono">
          {issues.map(({ p, msg, since, tone }) => {
            const isTier0 = p.tier === 0;
            const displayName = providerDisplayName(p.slug, p.name);
            return (
              <div
                key={p.slug}
                title={msg ? `${p.name} — ${msg}` : p.name}
                className={`flex items-center gap-3 px-3 py-2.5 min-w-0 hover:bg-surface2 ${
                  isTier0 || tone === 2 ? 'bg-bad/20' : ''
                }`}
              >
                <span className={`shrink-0 w-8 font-bold ${isTier0 ? 'text-bad' : 'text-white'}`}>
                  T{p.tier}
                </span>
                <span className="flex items-center gap-1 min-w-0 shrink-0 max-w-[20ch]">
                  <span className="text-white font-semibold truncate">{displayName}</span>
                  {p.description && <ProviderInfoTooltip description={p.description} />}
                </span>
                {p.source_type === 'external' ? (
                  <ExternalIndicator href={statusPageUrl(p)} label={p.name} />
                ) : (
                  <StatusGlow severity={tone}>{SEVERITY_LABEL[tone]}</StatusGlow>
                )}
                <span className="text-white shrink-0 whitespace-nowrap">
                  {since ? timeAgo(since) : 'new'}
                </span>
                {msg && <span className="text-white truncate flex-1 min-w-0 text-right">{msg}</span>}
                <span className="shrink-0 flex items-center gap-2">
                  <ExternalStatusIcon
                    href={statusPageUrl(p)}
                    label={`Open ${p.name} status page`}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </CommandPanel>
  );
}
